"use client";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import {
  AddressInput,
  Button,
  Count,
  Field,
  Input,
  Label,
  Listbox,
  ListboxLabel,
  ListboxOption,
  NavbarItem,
  NavbarLabel,
  NavbarSection,
  Panel,
} from "@/components";
import { EmptyState } from "./EmptyState";
import CartPayment from "./CartPayment";
import CartItems from "./CartItems";
import CartStepper from "./CartStepper";
import { CartSchema } from "../types";
import { useCartPanel } from "./useCartPanel";
import { useActiveCart } from "./useActiveCart";
import { WalletSchema } from "../../wallet/types";
import { useModalState } from "@/lib";
import ShippingRates from "./ShippingRates";
import FieldErrorMessage from "@/components/molecules/error-message";

export default function CartPanel({
  wallet,
}: {
  cart: CartSchema;
  wallet: WalletSchema;
}) {
  const pathname = usePathname();
  const { openModal, closeModal, modalIsOpen } = useModalState();

  const {
    cart,
    amountBreakdown,
    cartIsLoading,
    amountBreakdownIsLoading,
    mutateAccountBreakdown,
    mutateCart,
  } = useActiveCart();

  const isEmpty = cart?.ok === false || !cart?.lineItems?.length;
  const [step, setStep] = useState(0);
  const steps = ["Cart", "Shipping", "Payment"];

  const nextStep = () =>
    setStep((prev) => Math.min(prev + 1, steps.length - 1));
  const prevStep = () => setStep((prev) => Math.max(prev - 1, 0));

  const {
    register,
    setValue,
    onSubmit,
    loading,
    errors,
    clearErrors,
    getValues,
    saveAddress,
  } = useCartPanel(cart?._id ?? "", cart, mutateCart, () => nextStep());

  useEffect(() => {
    closeModal();
    setStep(0);
  }, [pathname]);

  if (!(pathname?.startsWith("/procure") || pathname.endsWith("/"))) {
    return null;
  }

  if (!amountBreakdown || !cart) {
    return null;
  }

  const renderStepContent = () => {
    switch (step) {
      case 0:
        return (
          <CartItems
            cart={cart}
            dataIsLoading={cartIsLoading || amountBreakdownIsLoading}
            callbackAfterItemSave={() => {
              mutateAccountBreakdown();
              mutateCart();
            }}
          />
        );
      case 1:
        return (
          <form onSubmit={onSubmit} className="py-2 px-3 space-y-6">
            <div className="space-y-2">
              <p className="text-base font-semibold -mb-1">Shipping Method</p>
              <p className="text-sm text-gray-600">
                Select your preferred shipping method
              </p>
              <Field className="w-full">
                <Listbox
                  defaultValue={cart?.shipmentMethod ?? ""}
                  className="w-full"
                  onChange={(shipmentMethod: string) => {
                    setValue("shipmentMethod", shipmentMethod);
                    clearErrors("shipmentMethod");
                  }}
                  inputRef={register("shipmentMethod").ref}
                  inputName={register("shipmentMethod").name}>
                  <ListboxOption value="" disabled>
                    <ListboxLabel>Select shipping method</ListboxLabel>
                  </ListboxOption>
                  <ListboxOption value={"SEA"}>
                    <ListboxLabel>SEA</ListboxLabel>
                  </ListboxOption>
                  <ListboxOption value={"AIR"}>
                    <ListboxLabel>AIR</ListboxLabel>
                  </ListboxOption>
                </Listbox>
                <FieldErrorMessage error={errors?.shipmentMethod} />
              </Field>
            </div>
            <div className="mb-4 space-y-2">
              <p className="text-base font-semibold -mb-1">Delivery Address</p>
              <p className="text-sm text-gray-600">
                Enter your preferred delivery address
              </p>
              <div className="flex justify-between items-center space-x-2">
                {/* <Field>
                <AddressInput
                  defaultValue=""
                  onSelectAddress={(deliveryAddress: string) => {
                    setValue("deliveryAddress", deliveryAddress);
                  }}
                />
              </Field> */}
                <Field className="w-full">
                  <Input
                    type="text"
                    placeholder="Delivery Address"
                    defaultValue={cart?.deliveryAddress ?? ""}
                    {...register("deliveryAddress")}
                  />
                </Field>
                <Button
                  color="white"
                  onClick={() => saveAddress(getValues().deliveryAddress)}
                  disabled={loading}
                  className="!h-11 !w-24 md:!w-[9.5rem] !border-zinc-300 flex">
                  <span className="md:hidden">Save</span>
                  <span className="max-sm:hidden">Save to Profile</span>
                </Button>
              </div>
              <FieldErrorMessage error={errors?.deliveryAddress} />
            </div>
            <ShippingRates />
          </form>
        );
      case 2:
        return (
          <CartPayment
            cart={cart}
            wallet={wallet}
            dataIsLoading={cartIsLoading || amountBreakdownIsLoading}
            amountBreakdown={amountBreakdown}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <NavbarSection>
        <NavbarItem onClick={openModal} large={false} cart>
          <NavbarLabel className="!font-semibold">Cart</NavbarLabel>
          <Count count={cart?.lineItems?.length ?? 0} space={false} />
        </NavbarItem>
      </NavbarSection>
      <Panel
        header={false}
        footer={false}
        open={modalIsOpen}
        openPanel={openModal}
        closePanel={closeModal}
        container="bg-gray-100 h-full">
        {isEmpty ? (
          <div className="flex flex-col justify-between">
            <EmptyState />
            <Button
              plain
              onClick={() => closeModal()}
              className="!text-sm !font-medium !text-dark">
              continue shopping
            </Button>
          </div>
        ) : (
          <div className="flex flex-col h-full">
            {/* Stepper Header */}
            <CartStepper
              step={step}
              onStepChange={(selected) =>
                selected === 2 ? onSubmit() : setStep(selected)
              }
            />

            {/* Step Content */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {renderStepContent()}
            </div>

            {/* Stepper Footer Buttons */}
            <div className="flex justify-between items-center border-t border-gray-300 px-4 py-3">
              <Button
                plain
                onClick={prevStep}
                disabled={step === 0}
                className="max-sm:hidden text-sm font-medium w-[50%]">
                Back
              </Button>
              <Button
                plain
                onClick={step === 0 ? closeModal : prevStep}
                className="md:hidden text-sm font-medium w-[50%]">
                Back
              </Button>
              {step < steps.length - 1 ? (
                <Button
                  onClick={() => {
                    if (step === 1) {
                      onSubmit();
                    } else {
                      nextStep();
                    }
                  }}
                  disabled={step === 3}
                  className="text-sm font-semibold w-[50%]">
                  Next
                </Button>
              ) : null}
            </div>
          </div>
        )}
      </Panel>
    </>
  );
}
